import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { Sort } from '@angular/material/sort';
import { PageEvent } from '@angular/material/paginator';
import { HotToastService } from '@ngxpert/hot-toast';
import { GenericListComponent } from '../../shared/generic-list/generic-list.component';
import { ColumnDefinition } from '../../../models/columnDefinition';
import { IssueService } from '../../../services/issue.service';
import { IssueListItem, IssueCategory } from '../../../models/issue';
import { IssueSearchRequest } from '../../../models/api/issueRequests';
import { IssueStatus, ISSUE_STATUS_LABELS } from '../../../models/enums/issueStatus.enum';
import { IssueType } from '../../../models/enums/issueType.enum';
import { IssuePriority } from '../../../models/enums/issuePriority.enum';
import { CurrentUserState } from '../../../state/current-user.state';

@Component({
  selector: 'app-issue-list',
  imports: [
    CommonModule, FormsModule, RouterLink, GenericListComponent,
    MatButtonModule, MatIconModule, MatFormFieldModule, MatSelectModule, MatInputModule
  ],
  template: `
    <div class="component-container">
      <div class="component-header">
        <h2>Issues</h2>
        @if (canCreate) {
          <button mat-raised-button color="primary" routerLink="create">
            <mat-icon>add</mat-icon>
            Report issue
          </button>
        }
      </div>

      <div class="component-box">
        <div class="filters">
          <mat-form-field appearance="fill">
            <mat-label>Search</mat-label>
            <input matInput [(ngModel)]="filterSearch" (keyup.enter)="applyFilters()" placeholder="Title or #number" />
          </mat-form-field>
          <mat-form-field appearance="fill" class="status-filter">
            <mat-label>Status</mat-label>
            <mat-select [(ngModel)]="filterStatuses" (selectionChange)="applyFilters()" multiple>
              @for (s of statuses; track s) { <mat-option [value]="s">{{ statusLabels[s] }}</mat-option> }
            </mat-select>
            @if (filterStatuses.length) {
              <button matSuffix mat-icon-button type="button" aria-label="Clear status filter"
                      (click)="clearStatuses($event)">
                <mat-icon>close</mat-icon>
              </button>
            }
          </mat-form-field>
          <mat-form-field appearance="fill">
            <mat-label>Type</mat-label>
            <mat-select [(ngModel)]="filterType" (selectionChange)="applyFilters()">
              <mat-option [value]="''">All</mat-option>
              @for (t of types; track t) { <mat-option [value]="t">{{ t }}</mat-option> }
            </mat-select>
          </mat-form-field>
          <mat-form-field appearance="fill">
            <mat-label>Category</mat-label>
            <mat-select [(ngModel)]="filterCategoryId" (selectionChange)="applyFilters()">
              <mat-option [value]="''">All</mat-option>
              @for (c of categories; track c.id) { <mat-option [value]="c.id">{{ c.name }}</mat-option> }
            </mat-select>
          </mat-form-field>
          <mat-form-field appearance="fill">
            <mat-label>Priority</mat-label>
            <mat-select [(ngModel)]="filterPriority" (selectionChange)="applyFilters()">
              <mat-option [value]="''">All</mat-option>
              @for (p of priorities; track p) { <mat-option [value]="p">{{ p }}</mat-option> }
            </mat-select>
          </mat-form-field>
          <mat-form-field appearance="fill">
            <mat-label>Verified</mat-label>
            <mat-select [(ngModel)]="filterVerified" (selectionChange)="applyFilters()">
              <mat-option [value]="''">All</mat-option>
              <mat-option value="true">Verified</mat-option>
              <mat-option value="false">Not verified</mat-option>
            </mat-select>
          </mat-form-field>
          <button mat-stroked-button type="button" (click)="load()">
            <mat-icon>refresh</mat-icon>
            Reload
          </button>
          <button mat-flat-button type="button" class="nrg-error-flat-button" (click)="resetFilters()">
            <mat-icon>clear</mat-icon>
            Reset
          </button>
        </div>
      </div>

      <app-generic-list
        [items]="issues"
        [columnDefinitions]="columnDefinitions"
        [serverSidePagination]="true"
        [totalItems]="totalItems"
        [currentPage]="currentPage - 1"
        [pageSize]="pageSize"
        [enableView]="true"
        [sortActive]="sortBy"
        [sortDirection]="ascending ? 'asc' : 'desc'"
        emptyMessage="No issues found"
        (viewClick)="onView($event)"
        (itemClick)="onView($event)"
        (sortChange)="onSortChange($event)"
        (pageChange)="onPageChange($event)">
      </app-generic-list>
    </div>
  `,
  styles: [`
    /* Search/filters in their own card, matching the search boxes across the app */
    .component-box {
      margin-bottom: 1rem;
      padding: 1rem;
      background-color: color-mix(in srgb, var(--mat-sys-primary), transparent 95%) !important;
      border-radius: var(--mat-expansion-container-shape, 12px);
    }
    .filters { display: flex; flex-wrap: wrap; gap: 12px; align-items: center; }
    .filters mat-form-field { width: 200px; }
    .filters .status-filter { width: 280px; }
  `]
})
export class IssueListComponent implements OnInit {
  private issueService = inject(IssueService);
  private router = inject(Router);
  private toast = inject(HotToastService);
  private currentUserState = inject(CurrentUserState);

  issues: IssueListItem[] = [];
  totalItems = 0;
  categories: IssueCategory[] = [];

  currentPage = 1;
  pageSize = 25;
  // Lowercase to match the column name matSort uses, so the restored sort header stays in sync.
  sortBy = 'number';
  ascending = false;

  filterType = '';
  filterCategoryId = '';
  filterPriority = '';
  filterSearch = '';
  /** '' = both, 'true' = verified only, 'false' = unverified only. Pair with Status=Staged for the promote queue. */
  filterVerified = '';

  statuses = Object.values(IssueStatus);
  statusLabels = ISSUE_STATUS_LABELS;
  types = Object.values(IssueType);
  priorities = Object.values(IssuePriority);

  // Status filter is multi-select; default selects every status except Closed and Rejected.
  private readonly defaultStatuses = this.statuses.filter(
    s => s !== IssueStatus.Closed && s !== IssueStatus.Rejected
  );
  filterStatuses: string[] = [...this.defaultStatuses];

  columnDefinitions: ColumnDefinition[] = [
    { propertyName: 'number', displayName: '#', dataType: 'number' },
    { propertyName: 'title', displayName: 'Title' },
    { propertyName: 'type', displayName: 'Type' },
    {
      propertyName: 'status',
      displayName: 'Status',
      dataType: 'badge',
      badgeMap: {
        Open: { label: 'Open', cssClass: 'issue-status-open' },
        Reviewed: { label: 'Reviewed', cssClass: 'issue-status-reviewed' },
        InProgress: { label: 'In Progress', cssClass: 'issue-status-inprogress' },
        Done: { label: 'Done', cssClass: 'issue-status-done' },
        Staged: { label: 'Staged', cssClass: 'issue-status-staged' },
        Released: { label: 'Released', cssClass: 'issue-status-released' },
        Closed: { label: 'Closed', cssClass: 'issue-status-closed' },
        Parked: { label: 'Parked', cssClass: 'issue-status-parked' },
        Rejected: { label: 'Rejected', cssClass: 'issue-status-rejected' }
      }
    },
    {
      propertyName: 'priority',
      displayName: 'Priority',
      dataType: 'badge',
      badgeMap: {
        Low: { label: 'Low', cssClass: 'issue-priority-low' },
        Medium: { label: 'Medium', cssClass: 'issue-priority-medium' },
        High: { label: 'High', cssClass: 'issue-priority-high' },
        Critical: { label: 'Critical', cssClass: 'issue-priority-critical' }
      }
    },
    { propertyName: 'categoryName', displayName: 'Category', sortable: false },
    { propertyName: 'reportedByUserName', displayName: 'Reporter', sortable: false },
    {
      // Blank until the client has confirmed the fix — an unverified issue has no badge at all,
      // which keeps the column quiet and makes the verified ones jump out.
      propertyName: 'verifiedEnvironment',
      displayName: 'Verified',
      dataType: 'badge',
      sortable: false,
      badgeMap: {
        Staging: { label: 'On staging', cssClass: 'issue-verified-staging' },
        Production: { label: 'In production', cssClass: 'issue-verified-production' }
      }
    },
    { propertyName: 'updatedDate', displayName: 'Last update', dataType: 'datetime' }
  ];

  get canCreate(): boolean {
    return this.currentUserState.canCreate('Issues', 'Issues List');
  }

  ngOnInit(): void {
    this.issueService.getCategories().subscribe({ next: c => (this.categories = c), error: () => {} });
    this.restoreState();
    this.load();
  }

  /** Re-apply the filters/sort/pagination saved when the user last left the list. */
  private restoreState(): void {
    const state = this.issueService.listState;
    if (!state) return;

    this.filterStatuses = [...state.filterStatuses];
    this.filterType = state.filterType;
    this.filterCategoryId = state.filterCategoryId;
    this.filterPriority = state.filterPriority;
    this.filterVerified = state.filterVerified;
    this.filterSearch = state.filterSearch;
    this.currentPage = state.currentPage;
    this.pageSize = state.pageSize;
    this.sortBy = state.sortBy;
    this.ascending = state.ascending;
  }

  /** Persist the current view so it can be restored after navigating away and back. */
  private saveState(): void {
    this.issueService.listState = {
      filterStatuses: [...this.filterStatuses],
      filterType: this.filterType,
      filterCategoryId: this.filterCategoryId,
      filterPriority: this.filterPriority,
      filterVerified: this.filterVerified,
      filterSearch: this.filterSearch,
      currentPage: this.currentPage,
      pageSize: this.pageSize,
      sortBy: this.sortBy,
      ascending: this.ascending
    };
  }

  load(): void {
    this.saveState();
    const request: IssueSearchRequest = {
      pageNumber: this.currentPage,
      pageSize: this.pageSize,
      sortBy: this.sortBy,
      ascending: this.ascending,
      statuses: this.filterStatuses.length ? this.filterStatuses : undefined,
      type: this.filterType || undefined,
      categoryId: this.filterCategoryId || undefined,
      priority: this.filterPriority || undefined,
      verified: this.filterVerified === '' ? undefined : this.filterVerified === 'true',
      search: this.filterSearch.trim() || undefined
    };
    this.issueService.search(request).subscribe({
      next: res => {
        this.issues = res.data;
        this.totalItems = res.totalCount;
      },
      error: () => this.toast.error('Error loading issues')
    });
  }

  applyFilters(): void {
    this.currentPage = 1;
    this.load();
  }

  clearStatuses(event: Event): void {
    event.stopPropagation(); // keep the suffix click from opening the select
    this.filterStatuses = [];
    this.applyFilters();
  }

  resetFilters(): void {
    this.filterStatuses = [...this.defaultStatuses];
    this.filterType = '';
    this.filterCategoryId = '';
    this.filterPriority = '';
    this.filterVerified = '';
    this.filterSearch = '';
    this.applyFilters();
  }

  onView(item: IssueListItem): void {
    this.router.navigate(['/issues', item.id]);
  }

  onSortChange(sort: Sort): void {
    if (sort.direction) {
      this.sortBy = sort.active;
      this.ascending = sort.direction === 'asc';
      this.load();
    }
  }

  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.load();
  }
}
